import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { toast } from 'react-hot-toast';
import { createFlowTransaction } from 'views/finance/moduleServices/flow_requests';
import SpinnerLoad from 'views/finance/moduleComponents/SpinnerLoad';
import DropdownCustom from 'views/finance/moduleComponents/DropdownCustom';
import DropdownCurrency from 'views/finance/moduleComponents/DropdownCurrency';
import { useFinanceProfile } from '../../../moduleServices/finance_profilecontext';
import { getL0Options, getL1Options, getL2Options, buildL0Key } from 'views/finance/moduleServices/helper_hierarchicalCategory';
import { formatDateLocal } from 'shared_items/formatConventions';

const ModalFlowAdd = ({ isOpen, onClose, onSuccess, categories = [], hierarchicalCategories = {} }) => {
  const { profile, currencyOptions } = useFinanceProfile();
  const defaultCategL0Label = profile?.default_categ_l0 || '';
  const defaultCurrency = profile?.default_currency || 'BRL';
  
  const getInitialState = () => {
    const defaultL0Key = defaultCategL0Label ? buildL0Key(defaultCategL0Label, 'Out') : '';
    return {
      date: formatDateLocal(new Date()),
      hour: new Date().toTimeString().split(' ')[0].slice(0, 5),
      desc: '',
      categ_l0: defaultL0Key,
      categ_l1: '',
      categ_l2: '',
      reference: '',
      flow_type: 'Out',
      flow_value: '',
      flow_currency: defaultCurrency,
      flow_account: ''
    };
  };
  
  const [newFlow, setNewFlow] = useState(getInitialState());
  const [submitting, setSubmitting] = useState(false);
  
  // Reset form when modal opens or when default changes
  useEffect(() => {
    if (isOpen) {
      setNewFlow(getInitialState());
    }
  }, [isOpen, defaultCategL0Label, defaultCurrency]);

  // Helper functions for new flat structure
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Reset dependent categories when parent changes
    if (name === 'categ_l0') {
      setNewFlow(prev => ({ 
        ...prev, 
        [name]: typeof value === 'object' ? value.value : value,
        categ_l1: '',
        categ_l2: ''
      }));
    } else if (name === 'categ_l1') {
      setNewFlow(prev => ({ 
        ...prev, 
        [name]: typeof value === 'object' ? value.value : value,
        categ_l2: ''
      }));
    } else if (name === 'categ_l2') {
      setNewFlow(prev => ({ 
        ...prev, 
        [name]: typeof value === 'object' ? value.value : value
      }));
    } else if (name === 'flow_type') {
      setNewFlow(prev => {
        const currentL0Label = prev.categ_l0 
          ? (hierarchicalCategories.by_l0?.[prev.categ_l0]?.name || '')
          : '';
        const newL0Key = currentL0Label ? buildL0Key(currentL0Label, value) : '';
        
        // Only keep L0 if the new key exists in hierarchicalCategories
        const keyExists = hierarchicalCategories.by_l0?.[newL0Key];
        
        return {
          ...prev,
          flow_type: value,
          categ_l0: keyExists ? newL0Key : '',
          categ_l1: '',
          categ_l2: ''
        };
      });
    } else {
      setNewFlow(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      const l0Name = hierarchicalCategories.by_l0?.[newFlow.categ_l0]?.name || '';
      const l1Name = hierarchicalCategories.by_l1?.[newFlow.categ_l1]?.name || '';
      const l2Name = hierarchicalCategories.by_l2?.[newFlow.categ_l2]?.name || '';
      const flowData = {
        ...newFlow,
        categ_l0: l0Name,
        categ_l1: l1Name,
        categ_l2: l2Name,
        flow_value: parseFloat(newFlow.flow_value),
        flow_account: newFlow.flow_account || null
      };

      await createFlowTransaction(flowData);
      toast.success('Transaction created successfully!');
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create transaction');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white p-4 md:p-6 rounded-2xl shadow w-80 md:w-96 relative">
        {submitting && (
          <div className="absolute inset-0 flex items-center justify-center z-10">
            <div className="bg-white/90 rounded-xl shadow-lg p-4 border border-gray-200">
              <SpinnerLoad />
            </div>
          </div>
        )}
        <button onClick={onClose} disabled={submitting} className={`absolute top-2 right-2 ${submitting ? 'opacity-50 cursor-not-allowed' : 'text-gray-500 hover:text-gray-700'}`}>
          <XMarkIcon className="h-6 w-6" />
        </button>
        <h2 className="text-lg font-semibold mb-4">Add New Transaction</h2>
        
        <form className="space-y-4" onSubmit={handleSubmit}>
          {/* Top Controls Row */}
          <div className="flex items-center gap-1 md:gap-1.5 flex-wrap min-w-0">
            {/* Flow Type Buttons */}
            <div className="bg-gray-100 rounded-full p-0.5 inline-flex flex-shrink-0">
              {['In', 'Out'].map((type, index) => (
                <button
                  key={type}
                  type="button"
                  disabled={submitting}
                  onClick={() => handleInputChange({ target: { name: 'flow_type', value: type } })}
                  className={`
                    px-3 py-1 text-xs font-medium transition-all
                    ${index === 0 ? 'rounded-l-full' : 'rounded-r-full'}
                    ${newFlow.flow_type === type
                      ? type === 'In'
                        ? 'text-white bg-green-400'
                        : 'text-white bg-red-400'
                      : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
                    }
                    ${submitting ? 'opacity-60 cursor-not-allowed' : ''}
                  `}
                >
                  {type}
                </button>
              ))}
            </div>

            {/* Account Input */}
            <input
              type="text"
              name="flow_account"
              value={newFlow.flow_account}
              onChange={handleInputChange}
              placeholder="Account"
              className="min-w-[80px] max-w-[150px] flex-1 px-2 py-1.5 border rounded-md text-xs"
              disabled={submitting}
            />

            {/* Category L0 Select */}
            <DropdownCustom
              options={getL0Options(hierarchicalCategories, newFlow.flow_type)}
              selectedValue={newFlow.categ_l0}
              onSelect={(option) => handleInputChange({ target: { name: 'categ_l0', value: option } })}
              placeholder="Select L0"
              width="w-24 md:w-32"
              disabled={submitting}
              buttonClassName="ml-0.5 flex-shrink-0"
            />
          </div>

          {/* Value Input */}
          <div className="relative mt-4 mb-6">
            <div className="flex items-center justify-end gap-2">
              <input
                type="number"
                name="flow_value"
                value={newFlow.flow_value}
                onChange={handleInputChange}
                placeholder="0.00"
                step="0.01"
                min="0"
                className="max-w-[200px] md:max-w-[300px] text-center text-4xl font-semibold py-3 appearance-none focus:outline-none bg-transparent [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                style={{ 
                  WebkitAppearance: 'none',
                  MozAppearance: 'textfield'
                }}
                disabled={submitting}
                required
              />
              <div className="flex-shrink-0">
                <DropdownCurrency
                  selectedValue={newFlow.flow_currency}
                  onSelect={(value) => setNewFlow(prev => ({ ...prev, flow_currency: value }))}
                  disabled={submitting}
                  usePortal={false}
                  options={currencyOptions}
                />
              </div>
            </div>
            <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent opacity-30"></div>
          </div>

          {/* Description */}
          <textarea
            name="desc"
            value={newFlow.desc}
            onChange={handleInputChange}
            placeholder="Description"
            rows={2}
            className="w-full px-2 py-1.5 border rounded-md text-sm"
            disabled={submitting}
            required
          />

          {/* Date and Time */}
          <div className="flex gap-2">
            <input
              type="date"
              name="date"
              value={newFlow.date}
              onChange={handleInputChange}
              className="w-1/2 px-2 py-1.5 border rounded-md text-sm"
              disabled={submitting}
              required
            />
            <input
              type="time"
              name="hour"
              value={newFlow.hour}
              onChange={handleInputChange}
              className="w-1/2 px-2 py-1.5 border rounded-md text-sm"
              disabled={submitting}
              required
            />
          </div>

          {/* Categories */}
          <div className="flex gap-2">
            <div className="flex-1">
              <DropdownCustom
                options={getL1Options(hierarchicalCategories, newFlow.categ_l0)}
                selectedValue={newFlow.categ_l1}
                onSelect={(option) => handleInputChange({ target: { name: 'categ_l1', value: option } })}
                placeholder="Select L1"
                disabled={submitting || !newFlow.categ_l0}
              />
            </div>
            <div className="flex-1">
              <DropdownCustom
                options={getL2Options(hierarchicalCategories, newFlow.categ_l1)}
                selectedValue={newFlow.categ_l2}
                onSelect={(option) => handleInputChange({ target: { name: 'categ_l2', value: option } })}
                placeholder="Select L2"
                disabled={submitting || !newFlow.categ_l1}
              />
            </div>
          </div>

          {/* Reference */}
          <input
            type="text"
            name="reference"
            value={newFlow.reference}
            onChange={handleInputChange}
            placeholder="Reference"
            className="w-full px-2 py-1.5 border rounded-md text-sm"
            disabled={submitting}
          />

          {/* Submit Button */}
          <button
            type="submit"
            disabled={submitting}
            className={`w-full h-10 text-white rounded-2xl ${submitting ? 'bg-green-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'}`}
          >
            {submitting ? 'Creating...' : 'Create Transaction'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ModalFlowAdd;
